import { Button as BaseButton } from "@base-ui/react/button";
import * as stylex from "@stylexjs/stylex";
import { type ComponentProps, type ReactElement, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "../tokens.stylex.ts";
import { color, font, fontSize, fontWeight, media, motion, radius, space, texture } from "../tokens.stylex.ts";
import { Spinner } from "./Spinner.tsx";
import { surface } from "./surface.styles.ts";
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";
/** Same shape as a TanStack Query mutation's `status`, so `status={mutation.status}` just works. */
export type ButtonStatus = "idle" | "pending" | "success" | "error";
type BaseButtonProps = ComponentProps<typeof BaseButton>;
type ClickEvent = Parameters<NonNullable<BaseButtonProps["onClick"]>>[0];
export interface ButtonProps extends Omit<BaseButtonProps, "className" | "style" | "render" | "onClick"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Drive the button through an async action. `success` and `error` flash a
   * confirmation for a moment, then the button settles back to its label.
   */
  status?: ButtonStatus;
  /** Shorthand for `status="pending"`. */
  loading?: boolean;
  /**
   * Return a promise to have the button track it on its own: pending while it
   * runs, then success or error. Rejections are still re-thrown.
   */
  onClick?: (event: ClickEvent) => unknown;
  /** Label shown after a successful action. */
  successLabel?: ReactNode;
  /** Label shown after a failed action. */
  errorLabel?: ReactNode;
  /** Icon rendered before the label. */
  icon?: ReactNode;
  /** Square, icon-only button. Provide `aria-label`. */
  iconOnly?: boolean;
  fullWidth?: boolean;
  /** Render as another element, e.g. a router `<Link>`. Set `nativeButton={false}` for non-buttons. */
  render?: ReactElement;
}

/** Fast actions skip the spinner and go straight to their outcome. */
const PENDING_DELAY = 150;
const FEEDBACK_DURATION = {
  success: 2000,
  error: 3500
} as const;
export function Button({
  variant = "secondary",
  size = "md",
  status,
  loading = false,
  onClick,
  successLabel = "Done",
  errorLabel = "Failed",
  icon,
  iconOnly = false,
  fullWidth = false,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  const tracked = usePromiseStatus();
  const requested: ButtonStatus = tracked.status !== "idle" ? tracked.status : loading ? "pending" : status ?? "idle";
  const phase = useDisplayPhase(requested, tracked.reset);
  const busy = requested === "pending";
  const glyphSize = size === "sm" ? 12 : 14;
  const content: Record<ButtonStatus, ReactNode> = {
    idle: <>
        {icon}
        {!iconOnly && children}
      </>,
    pending: <Spinner size={glyphSize} />,
    success: <>
        <SuccessGlyph size={glyphSize} />
        {!iconOnly && successLabel}
      </>,
    error: <>
        <ErrorGlyph size={glyphSize} />
        {!iconOnly && errorLabel}
      </>
  };
  const morph = !iconOnly && !fullWidth;
  const {
    layers,
    onLayerExited
  } = useLayers(phase);
  const width = useContentWidth(morph, phase === "pending");
  const feedback = phase === "success" || phase === "error" ? phase : null;
  return <>
      <BaseButton {...rest} onClick={event => {
      const result = onClick?.(event);
      if (isPromiseLike(result)) tracked.track(result);
    }} disabled={disabled || busy} focusableWhenDisabled={busy} aria-busy={busy || undefined} {...stylex.props(styles.base, variant !== "ghost" && surface.raised, variant !== "ghost" && surface.sm, variant === "primary" && surface.accent, variant !== "ghost" && styles.pressable, sizes[size], variants[variant], feedback && (variant === "ghost" ? ghostFeedback[feedback] : feedbackFaces[feedback]), (busy || feedback) && styles.busy, iconOnly && iconOnlySizes[size], fullWidth && styles.fullWidth)}>
        <span {...stylex.props(styles.viewport, width.value !== null && styles.width(width.value))}>
          {layers.map(layer => <span key={layer.key} ref={layer.exiting ? undefined : width.ref} aria-hidden={layer.exiting || undefined} onAnimationEnd={layer.exiting ? event => event.target === event.currentTarget && onLayerExited(layer.key) : undefined} {...stylex.props(styles.layer, layer.exiting ? styles.exit : !layer.initial && styles.enter)}>
              {content[layer.phase]}
            </span>)}
        </span>
      </BaseButton>
      {requested !== "idle" && <output className="x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr x1hyvwdk xuxw1ft">
          {feedback === "success" ? successLabel : feedback === "error" ? errorLabel : null}
        </output>}
    </>;
}
function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return typeof value === "object" && value !== null && "then" in value && typeof value.then === "function";
}

/** Status of the latest promise returned from `onClick`; older runs are ignored. */
function usePromiseStatus() {
  const [status, setStatus] = useState<ButtonStatus>("idle");
  const run = useRef(0);
  return {
    status,
    reset: useCallback(() => setStatus("idle"), []),
    track(promise: PromiseLike<unknown>) {
      const id = ++run.current;
      setStatus("pending");
      // Re-throwing keeps a rejected action as visible as it was without the button.
      void promise.then(() => id === run.current && setStatus("success"), (error: unknown) => {
        if (id === run.current) setStatus("error");
        throw error;
      });
    }
  };
}

/**
 * What the button shows for the requested status: the spinner only appears
 * once an action outlasts `PENDING_DELAY`, and success/error fall back to the
 * label after a moment even if the caller's status stays put.
 */
function useDisplayPhase(requested: ButtonStatus, onSettled: () => void): ButtonStatus {
  const [prev, setPrev] = useState(requested);
  const [settled, setSettled] = useState(false);
  const [pendingShown, setPendingShown] = useState(false);
  if (requested !== prev) {
    setPrev(requested);
    setSettled(false);
    if (requested !== "pending") setPendingShown(false);
  }
  useEffect(() => {
    if (requested === "pending") {
      const timer = setTimeout(() => setPendingShown(true), PENDING_DELAY);
      return () => clearTimeout(timer);
    }
    if (requested === "success" || requested === "error") {
      const timer = setTimeout(() => {
        setSettled(true);
        onSettled();
      }, FEEDBACK_DURATION[requested]);
      return () => clearTimeout(timer);
    }
  }, [requested, onSettled]);
  if (settled) return "idle";
  if (requested === "pending" && !pendingShown) return "idle";
  return requested;
}
interface Layer {
  key: number;
  phase: ButtonStatus;
  initial: boolean;
  exiting: boolean;
}

/** Keeps the outgoing content mounted while it animates out. */
function useLayers(phase: ButtonStatus) {
  const [layers, setLayers] = useState<Layer[]>(() => [{
    key: 0,
    phase,
    initial: true,
    exiting: false
  }]);
  const current = layers[layers.length - 1]!;
  if (current.phase !== phase) {
    setLayers([
    // Only the most recent outgoing layer is worth animating.
    {
      ...current,
      exiting: true
    }, {
      key: current.key + 1,
      phase,
      initial: false,
      exiting: false
    }]);
  }
  return {
    layers,
    onLayerExited: (key: number) => setLayers(all => all.filter(layer => layer.key !== key))
  };
}

/**
 * Width of the current content, so the button can animate between labels.
 * While `hold` is set (pending) it keeps the last width, so the spinner
 * doesn't collapse the button.
 */
function useContentWidth(enabled: boolean, hold: boolean) {
  const [value, setValue] = useState<number | null>(null);
  const [node, setNode] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!enabled || hold || !node) return;
    const measure = () => setValue(Math.ceil(node.getBoundingClientRect().width));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, hold, node]);
  return {
    value: enabled ? value : null,
    ref: setNode
  };
}
function SuccessGlyph({
  size
}: {
  size: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden className="x2lah0s xbh8q5q x73je2i xhr4kjn x1owpc8m x1f6yumg">
      <circle cx="8" cy="8" r="6.5" pathLength={1} transform="rotate(-90 8 8)" className="xnq1ati x1a497qr x1aquc0h x9dz2be xxn97zd xskzprw" />
      <path d="M5.4 8.3 7.2 10.1 10.6 6.3" pathLength={1} className="xnq1ati x1a497qr x1aquc0h xxn97zd xskzprw x1snll8 xw6taxk" />
    </svg>;
}
function ErrorGlyph({
  size
}: {
  size: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden className="x2lah0s xbh8q5q x73je2i xhr4kjn x1owpc8m x1f6yumg">
      <circle cx="8" cy="8" r="6.5" pathLength={1} transform="rotate(-90 8 8)" className="xnq1ati x1a497qr x1aquc0h x9dz2be xxn97zd xskzprw" />
      <path d="M8 4.9V8.6" pathLength={1} className="xnq1ati x1a497qr x1aquc0h xxn97zd xskzprw x1snll8 xw6taxk" />
      <circle cx="8" cy="11" r="0.9" fill="currentColor" stroke="none" className="x1qkzszz x7q002m x1aquc0h x5hsz1j xtmkasn xxn97zd xskzprw" />
    </svg>;
}
const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";
const slideIn = "x1lr47il-B";
const slideOut = "x10fodll-B";
const fadeIn = "xw2c1lr-B";
const fadeOut = "x1mi9bte-B";
const draw = "x1x7p1ci-B";
const pop = "x1ctfnrf-B";
const styles = {
  base: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kOIVth: "x1a62zme",
    kmuXW: "x2lah0s",
    kMv6JI: "x1wv6yj",
    k63SB2: "x1219144",
    kLWn49: "xo5v014",
    khDVqt: "xuxw1ft",
    kybGjl: "x1hl2dhg",
    kaIpWk: "xoazux2",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kkrTdU: "x1ypdohk x1s07b3s",
    kSiTet: "x1hc1fzr xk0mbs4",
    kfSwDN: "x87ps6o",
    k7VYTy: "xpdipgo",
    k1ekBW: "xv65o4f",
    kIyJzY: "x7updtu",
    kAMwcw: "xyda15a",
    kMeerF: "x1de99jn",
    k3XXqK: "x1t137rt x9v5kkp",
    kjBf7l: "x15xi569",
    kInvED: "x1hl8ikr",
    $$css: true
  },
  pressable: {
    "--slab": "xbq946m x12n7gj",
    k3aq6I: "x1x80292",
    $$css: true
  },
  busy: {
    kSiTet: "x1hc1fzr",
    kkrTdU: "xt0e3qv",
    $$css: true
  },
  fullWidth: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  viewport: {
    k1xSpc: "xrvj5dj",
    kSGwAc: "xkh2ocl",
    kAPf3g: "x1o2pa38",
    kXHlph: "x1plvlek",
    kORKVm: "x1s73dr8",
    k1ekBW: "xxrbq2n",
    kIyJzY: "xchbrx7 x12w9bfk",
    kAMwcw: "xyda15a",
    $$css: true
  },
  width: (width: number) => [{
    kzqmXN: width != null ? "x5lhr3w" : width,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(width)
  }],
  layer: {
    kJuA4N: "x15r89dc",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1a62zme",
    $$css: true
  },
  enter: {
    kKVMdj: "x1tjbflr x73z3c0",
    k44tkh: "x1g18oiy",
    kyAemX: "xxn97zd",
    $$css: true
  },
  exit: {
    kKVMdj: "x8577zs x1d1cljx",
    k44tkh: "x5hsz1j",
    kyAemX: "xxn97zd",
    kWV6AL: "x10e4vud",
    kfzvcC: "x47corl",
    $$css: true
  }
};

// Coarse pointers get finger-sized targets (44px is the iOS/Material minimum).
const sizes = {
  sm: {
    kZKoxP: "x1fgtraw x8wfcom",
    kg3NbH: "x1uu5im9",
    kGuDYH: "xket8oi",
    $$css: true
  },
  md: {
    kZKoxP: "x90ne7k x8tjdam",
    kg3NbH: "xsef1i0 x1ch7e46",
    kGuDYH: "xluwven xcb8g08",
    $$css: true
  }
};
const iconOnlySizes = {
  sm: {
    kzqmXN: "xgd8bvy x1t7s7q3",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  md: {
    kzqmXN: "x1691je0 xi1r4rr",
    kg3NbH: "xnjsko4",
    $$css: true
  }
};
const variants = {
  primary: {
    "--face-bg": "x748x8c x1nt98fo",
    "--face-border": "x1rfjqpn x1a9dfxf",
    kMwMTN: "x1n5o0sv",
    $$css: true
  },
  secondary: {
    "--face-bg": "x1upr4va xz2ku1l",
    "--face-border": "x9lrtta xxk1jwz",
    kMwMTN: "x9aqtqa",
    $$css: true
  },
  ghost: {
    kWkggS: "xjbqb8w xab4ycn xcc3ivb",
    kVAM5u: "x9r1u3d",
    kMwMTN: "x187ovcu xv3e1pb",
    $$css: true
  },
  danger: {
    "--face-bg": "x1upr4va xlvm847",
    "--face-border": "x1raya8z xnba7cm",
    "--face-dot": "x1y7s6vt",
    "--slab-bg": "x1gpgqo3",
    "--slab-border": "xdr542g",
    kMwMTN: "x1xcodwn",
    $$css: true
  }
};

/**
 * Outcome faces mirror the accent recipe: a solid status face (dot texture
 * inverted) resting on a slab in the soft tint of the same tone. Inverse text
 * stays legible on both themes' status colors.
 */
const feedbackFaces = {
  success: {
    "--face-bg": "xpvalor",
    "--face-border": "x1tduo4w",
    "--face-dot": "x1tfdxok",
    "--slab-bg": "xna4wdy",
    "--slab-border": "x1dcnwlw",
    "--slab-hatch": "x100daw0",
    kMwMTN: "x14f1h33",
    $$css: true
  },
  error: {
    "--face-bg": "x1j6b88p",
    "--face-border": "x1rkrvrg",
    "--face-dot": "x1tfdxok",
    "--slab-bg": "x1gpgqo3",
    "--slab-border": "xdr542g",
    "--slab-hatch": "x100daw0",
    kMwMTN: "x14f1h33",
    $$css: true
  }
};
const ghostFeedback = {
  success: {
    kWkggS: "xiltt8s",
    kMwMTN: "x14f1h33",
    $$css: true
  },
  error: {
    kWkggS: "x1dobq8r",
    kMwMTN: "x14f1h33",
    $$css: true
  }
};